import { useMemo, useState } from 'react'

const integerFormatter = new Intl.NumberFormat('zh-CN')
const CIRCUMFERENCE = 2 * Math.PI * 44

function formatNumber(value) {
  return integerFormatter.format(value)
}

function DonutChart({ title, eyebrow, items, centerValue, centerLabel, note, headerControl = null, className = '' }) {
  const [activeIndex, setActiveIndex] = useState(null)
  const total = useMemo(() => items.reduce((sum, item) => sum + item.value, 0), [items])
  let offset = 0
  const active = activeIndex === null ? null : items[activeIndex]

  return (
    <article className={`chart-card donut-card ${className}`}>
      <header>
        <div><span>{eyebrow}</span><h3>{title}</h3></div>
        {headerControl || <small>悬停查看数据</small>}
      </header>
      <div className="donut-layout">
        <div className="donut-plot" onMouseLeave={() => setActiveIndex(null)}>
          <svg viewBox="0 0 120 120" role="img" aria-label={`${title}，总计 ${centerValue ?? total}`}>
            <circle className="donut-track" cx="60" cy="60" r="44" />
            {items.map((item, index) => {
              const length = (item.value / total) * CIRCUMFERENCE
              const dashOffset = -offset
              offset += length
              return (
                <circle
                  key={item.label}
                  className={`donut-segment ${activeIndex === index ? 'active' : ''}`}
                  cx="60"
                  cy="60"
                  r="44"
                  stroke={item.color}
                  strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
                  strokeDashoffset={dashOffset}
                  tabIndex="0"
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  onBlur={() => setActiveIndex(null)}
                  aria-label={`${item.label} ${formatNumber(item.value)}，${item.share.toFixed(1)}%`}
                />
              )
            })}
          </svg>
          <div className="donut-center"><strong>{centerValue ?? formatNumber(total)}</strong><span>{centerLabel || '总计'}</span></div>
          <div className={`chart-tooltip ${active ? 'visible' : ''}`} role="status">
            {active ? <><b>{active.label}</b><strong>{formatNumber(active.value)}</strong><span>{active.share.toFixed(1)}%</span></> : null}
          </div>
        </div>
        <div className="chart-legend">
          {items.map((item, index) => (
            <button
              type="button"
              key={item.label}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
              onClick={() => setActiveIndex(index)}
            >
              <i style={{ background: item.color }} />
              <span>{item.label}</span>
              <b>{item.share.toFixed(1)}%</b>
            </button>
          ))}
        </div>
      </div>
      {note ? <p className="chart-note">{note}</p> : null}
    </article>
  )
}

function ScopeToggle({ value, onChange, ariaLabel, options }) {
  return (
    <div className="chart-scope-toggle" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={value === option.value ? 'active' : ''}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function HistoryCollaboration({ scopes }) {
  const [scope, setScope] = useState('all')
  const current = scopes[scope]

  const control = (
    <ScopeToggle
      value={scope}
      onChange={setScope}
      ariaLabel="历史合作占比分母"
      options={[{ value: 'all', label: '总资源池' }, { value: 'internal', label: '在库资源' }]}
    />
  )

  return (
    <DonutChart
      title="历史合作占比"
      eyebrow={current.eyebrow}
      items={current.items}
      centerValue={current.centerValue}
      centerLabel={current.centerLabel}
      note={current.note}
      headerControl={control}
      className="collaboration-chart-card"
    />
  )
}

function HorizontalBars({ title, eyebrow, items, note, headerControl = null, className = '' }) {
  const [activeIndex, setActiveIndex] = useState(null)
  const max = Math.max(...items.map((item) => item.value))
  const active = activeIndex === null ? null : items[activeIndex]
  return (
    <article className={`chart-card bar-chart-card ${className}`}>
      <header>
        <div><span>{eyebrow}</span><h3>{title}</h3></div>
        {headerControl || <small>悬停查看数据</small>}
      </header>
      <div className="horizontal-bars" onMouseLeave={() => setActiveIndex(null)}>
        {items.map((item, index) => (
          <button
            type="button"
            key={item.label}
            onMouseEnter={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
            onBlur={() => setActiveIndex(null)}
            onClick={() => setActiveIndex(index)}
          >
            <span>{item.label}</span>
            <i><b style={{ width: `${(item.value / max) * 100}%`, background: item.color }} /></i>
            <strong>{formatNumber(item.value)}</strong>
            <small>{item.share.toFixed(1)}%</small>
          </button>
        ))}
        <div className={`bar-tooltip ${active ? 'visible' : ''}`} role="status">
          {active ? <><b>{active.label}</b><span>{formatNumber(active.value)} · {active.share.toFixed(1)}%</span></> : null}
        </div>
      </div>
      {note ? <p className="chart-note">{note}</p> : null}
    </article>
  )
}

function PlatformDistribution({ scopes }) {
  const [scope, setScope] = useState('external')
  const current = scopes[scope]
  return (
    <HorizontalBars
      title="达人平台分布"
      eyebrow={current.eyebrow}
      items={current.items}
      note={current.note}
      className="platform-chart-card"
      headerControl={(
        <ScopeToggle
          value={scope}
          onChange={setScope}
          ariaLabel="达人平台分布资源范围"
          options={[{ value: 'external', label: '外部资源' }, { value: 'internal', label: '在库资源' }]}
        />
      )}
    />
  )
}

function CreatorTypeDistribution({ scopes }) {
  const [scope, setScope] = useState('external')
  const current = scopes[scope]
  return (
    <DonutChart
      title="达人类型分布"
      eyebrow={current.eyebrow}
      items={current.items}
      centerValue={current.centerValue}
      centerLabel={current.centerLabel}
      note={current.note}
      className="creator-type-chart-card"
      headerControl={(
        <ScopeToggle
          value={scope}
          onChange={setScope}
          ariaLabel="达人类型分布资源范围"
          options={[{ value: 'external', label: '外部资源' }, { value: 'internal', label: '在库资源' }]}
        />
      )}
    />
  )
}

function FollowerTierDistribution({ scopes }) {
  const [scope, setScope] = useState('all')
  const current = scopes[scope]
  return (
    <DonutChart
      title="粉丝量层级分布"
      eyebrow={current.eyebrow}
      items={current.items}
      centerValue={current.centerValue}
      centerLabel={current.centerLabel}
      note={current.note}
      className="follower-tier-chart-card"
      headerControl={(
        <ScopeToggle
          value={scope}
          onChange={setScope}
          ariaLabel="粉丝量层级分布资源范围"
          options={[{ value: 'all', label: '全部资源' }, { value: 'internal', label: '在库资源' }]}
        />
      )}
    />
  )
}

function SingleMeter({ item }) {
  const [active, setActive] = useState(false)
  const fraction = item.value / item.total
  return (
    <button
      type="button"
      className="single-meter"
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      aria-label={`${item.label} Apple 垂类 ${item.value} / ${item.total}，${item.share.toFixed(1)}%`}
    >
      <span className="meter-ring" style={{ background: `conic-gradient(${item.color} 0 ${fraction * 100}%, #e8e8ed ${fraction * 100}% 100%)` }}>
        <i><strong>{item.share.toFixed(1)}%</strong><small>Apple 垂类</small></i>
      </span>
      <b>{item.label}</b>
      <small>{formatNumber(item.value)} / {formatNumber(item.total)} 位</small>
      <span className={`meter-tooltip ${active ? 'visible' : ''}`}>Apple 垂类 {item.value} 位</span>
    </button>
  )
}

function AppleComparison({ items }) {
  return (
    <article className="chart-card apple-chart-card">
      <header><div><span>方向匹配</span><h3>Apple 垂类占比</h3></div><small>分池计算</small></header>
      <div className="apple-meter-grid">{items.map((item) => <SingleMeter key={item.label} item={item} />)}</div>
      <p className="chart-note">外部全池为 61 / 637（9.6%）；主图采用“外部初筛达人”口径，避免与在库重复。</p>
    </article>
  )
}

export default function ResourceCharts({ data }) {
  return (
    <div className="resource-chart-grid">
      <PlatformDistribution scopes={data.platforms} />
      <CreatorTypeDistribution scopes={data.creatorTypes} />
      <FollowerTierDistribution scopes={data.followerTiers} />
      <HorizontalBars
        title="达人其他合作品牌比例预估"
        eyebrow="按公开主页与内容检出记录估算"
        items={data.smartHomeBrands}
        note="以 28 位后期可考虑合作资源为样本，16 位检出智能家居品牌记录；品牌可多选，比例不可相加，公开未检出不代表从未合作。"
      />
      <AppleComparison items={data.apple} />
      <HistoryCollaboration scopes={data.collaboration} />
    </div>
  )
}
